/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { forkJoin } from 'rxjs';
import { Billing, BillingItem } from '@src/app/model/billing';
import { Cycle } from '@src/app/model/cycle';
import { Field } from '@src/app/shared/field/Field';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { required } from '@src/app/shared/validators';
import { BillingService } from '@src/app/modules/budget/billing.service';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { MyUnitService } from '@src/app/modules/unit/my-unit.service';
import { Unit } from '@src/app/model/unit';
import { Schedule } from '@src/app/model/schedule';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { BCode } from '@src/app/model/bcode';
import { formatCurrency } from '@angular/common';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { BillingPageNavRoute } from '@src/app/routes/app-router';
import { InvoicePageNavRoute } from '@src/app/routes/invoice-page-nav';

@Component({
    selector: 'app-billing-page',
    templateUrl: './billing-page.component.html',
    styleUrls: ['./billing-page.component.scss'],
    imports: [FormPageComponent]
})
export class BillingPageComponent extends AbstractPageComponent {
    private billingSvc = inject(BillingService);
    private cycleSvc = inject(CycleService);
    private bcodeSvc = inject(BCodeService);
    private scheduleSvc = inject(ScheduleService);
    private myUnitSvc = inject(MyUnitService);


    units: Unit[] = [];
    schedules: Schedule[] = [];
    bcodes: BCode[] = [];

    invoiceLink = FormNavLinkComponent.make('Billed', InvoicePageNavRoute, o => ({ billingId: o.id }),
        { formColumn: 2, value: 'billedTotal', calculateValue: (o) => {
            if (o['totalItems'] > 0) {
                return `${formatCurrency(o['billedTotal'], 'EN-ie', '€')} (${o['totalItems']})`
            }
            return '';
        }
    });

    outstandingLink = FormNavLinkComponent.make('Outstanding', InvoicePageNavRoute,
        o => ({ billingId: o.id, outstanding: '>=0.01' }),
        { formColumn: 2, value: 'outstandingTotal',
            calculateValue: (o) => {
                if (o['totalItems'] > 0) {
                    return `${formatCurrency(o['outstandingTotal'], 'EN-ie', '€')} (${o['outstandingItems']})`
                }
                return '';
            }
        }
    );

    fieldSet = new FieldSet({
        fields: [
            FieldMaker.id(),
            FieldMaker.rev(),
            FieldMaker.nameControl({ validators: [required] }),
            FormPicklistComponent.make('Cycle', 'cycleId', 'cycle', { service: this.cycleSvc }, { disable: true }),
            FormPicklistComponent.make('Status', 'statusId', '', { items: Cycle.statuses }, { disable: true }),
            this.invoiceLink,
            this.outstandingLink,
            FieldMaker.notes()
        ],
        formLayout: [{ cells: [{ width: '30%' }, { width: '20%' }, { width: '40%' }, { width: '10%' }] }],
    })

    config = new FormConfig({
        navRoute: BillingPageNavRoute,
        docPath: 'omc-books/budgeting/billing',
        title: $localize`Billing`,
        help: $localize`Billings take an amount of monies and raise service charges apportioned per unit for the total amount.`,
        readonly: true,
        fieldSet: this.fieldSet,
        service: this.billingSvc,
        mode: 'list',
        beforeEdit: this.beforeEdit.bind(this),
        configReady: this.configReady,
        allowDelete: false,
        allowNew: false,
    });

    constructor() {
        super();
        this.getUnitSetup();
    }

    getUnitSetup() {
        forkJoin({
            schedules: this.scheduleSvc.get(true),
            bcodes: this.bcodeSvc.get(true),
            units: this.myUnitSvc.get(),
        }).subscribe(result => {
            this.units = result.units as Unit[];
            this.schedules = (result.schedules as Schedule[]);
            this.bcodes = (result.bcodes as BCode[]);
            this.configReady.next(null);
        })
    }

    unitPercent(unit: Unit, bi: BillingItem) {
        const portion = unit.portions.find(p => p.scheduleId === bi.scheduleId);
        return portion ? portion.percent : 0;
    }

    billingItemFactory(bi: BillingItem) {
        const flds = [
            FieldMaker.id({ visible: Field.noShow }),
            FormPicklistComponent.make('Schedule', 'scheduleId', 'schedule',
                { service: this.scheduleSvc },
                {
                    disable: true,
                    footer: { text: 'Total', style: 'font-weight:500; text-align: right' }
                }),
            FormPicklistComponent.make('Budget Code', 'bCodeId', null,
                { items: this.bcodes },
                {
                    disable: true,
                    footer: { text: 'Total', style: 'font-weight:500; text-align: right' }
                }),
            FormNumberComponent.makeCurrency('Total', 'total'),
            FormNumberComponent.makeCurrency('Billed', 'billed'),
        ];
        for (const u of this.units) {
            const toolTip = bi ? ` ${(Math.round(this.unitPercent(u, bi) * 100000) / 1000)}% of ${bi.total}` : '';
            flds.push(FormNumberComponent.makeCurrency('Unit ' + u.name, 'unit-' + u.id, { toolTip }));
        }
        return flds;
    }
    getKey(bi: BillingItem) {
        return (bi.scheduleId as string) + (bi.bCodeId as string);
    }

    beforeEdit(o: Billing) {
        o.units = this.units;
        for (const u of o.units) {
            let uTotal = 0;
            //for (const s of this.schedules) {
            for (const bi of o.items) {
                const uPortion = this.getAmount(o, u, bi);

                u[this.getKey(bi)] = uPortion;
                uTotal += uPortion;

                //o['sched' + s.id + '-' + u.id] = uPortion;
            }
            for (const bi of o.items) {
                bi['unit-' + u.id] = this.getAmount(o, u, bi)
            }
            u['total'] = uTotal;

            o['total-' + u.id] = uTotal;
        }

        return o;
    }

    getAmount(billing: Billing, unit: Unit, charge: BillingItem) {
        //const charge = billing.items.find(s => s.scheduleId === schedule.id);
        if (charge) {
            return charge.total * this.unitPercent(unit, charge);
        } else {
            console.error('No charge for schedule', { billing, unit, charge });
            return 0;
        }
    }
}
